// Rodeo Bull Riding bonus round — mirrors the Volcano Boarding mechanic:
// the character rides a galloping bull down a dirt arena, steering left/right
// to collect the best out of 100 córdoba coins while dodging barrels. A
// can't-lose run: reaching the finish ends it, only the coin count varies.
import { GAME } from "./config";
import { ready } from "./assets";
import { drawBullRider } from "./rider";

const { WIDTH, HEIGHT } = GAME;
const TOTAL_COINS = 100;
const COURSE_LEN = 5200;
const FINISH_LINE = COURSE_LEN - 160;
const PLAYER_SCREEN_Y = 360;   // bull+rider stay here; arena scrolls past

export default class RodeoGame {
  constructor(canvas, { character, assets, audio, callbacks }) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.ctx.imageSmoothingEnabled = true;
    this.char = character;
    this.assets = assets || {};
    this.audio = audio || null;
    this.cb = callbacks || {};
    this.running = false;
    this.initState();
    this.buildCourse();
    this.buildCrowd();
    this.bindKeys();
  }

  initState() {
    this.input = { left: false, right: false };
    this.ended = false;
    this.paused = true;
    this.frame = 0;
    this.shake = 0;
    this.px = WIDTH / 2;
    this.depth = 0;
    this.vy = 5.2;
    this.maxVy = 10.5;
    this.wobble = 0;
    this.collected = 0;
    this.dust = [];
    this.sparks = [];
  }

  reset() { this.initState(); this.buildCourse(); }

  begin() { this.paused = false; this.audio?.resume(); }

  buildCourse() {
    this.coins = [];
    const startD = 380;
    const span = FINISH_LINE - 360 - startD;
    const pathX = (i) => {
      const t = i * 0.5;
      const x = WIDTH / 2 + Math.sin(t * 0.6) * 300 + Math.sin(t * 0.17) * 90;
      return Math.max(90, Math.min(WIDTH - 90, x));
    };
    for (let i = 0; i < TOTAL_COINS; i++) {
      const d = startD + (i / (TOTAL_COINS - 1)) * span;
      this.coins.push({ x: pathX(i), d, taken: false });
    }
    // Barrels laid out BESIDE the coin path (alternating sides) and offset in
    // depth so they sit in the gaps between coins — the player weaves to grab
    // coins while dodging, instead of barrels landing right on top of coins.
    this.barrels = [];
    const nB = 15;
    for (let k = 0; k < nB; k++) {
      const i = 5 + (k / nB) * (TOTAL_COINS - 10);
      const d = startD + (i / (TOTAL_COINS - 1)) * span + span * 0.012;
      const side = k % 2 === 0 ? 1 : -1;
      let bx = pathX(i + 1.5) + side * (160 + Math.random() * 70);
      bx = Math.max(110, Math.min(WIDTH - 110, bx));
      this.barrels.push({ x: bx, d, r: 30 + Math.random() * 8 });
    }
  }

  buildCrowd() {
    this.crowd = [];
    const skins = ["#f0d0b0", "#e8c39a", "#d4a373", "#a9744f", "#8a5a3b"];
    const shirts = ["#e05c4a", "#4a86e0", "#f2c14e", "#54c07a", "#c064d6", "#e8926b", "#ffffff", "#2ec4b6", "#ef5da8"];
    const hats = ["#c62828", "#1565c0", "#f9a825", "#2e7d32", "#5d4037"];
    for (let i = 0; i < 48; i++) {
      this.crowd.push({
        side: i % 2,
        col: (i >> 1) % 2,
        y: 24 + ((i >> 2)) * 40 + (i % 2) * 12,
        skin: skins[i % skins.length],
        shirt: shirts[(i * 3) % shirts.length],
        hat: (i * 7) % 5 < 2,
        hatC: hats[i % hats.length],
        bob: Math.random() * Math.PI * 2,
      });
    }
  }

  bindKeys() {
    this._onKey = (e, down) => {
      const c = e.code;
      if (c === "ArrowLeft" || c === "KeyA") this.input.left = down;
      else if (c === "ArrowRight" || c === "KeyD") this.input.right = down;
      else return;
      e.preventDefault();
    };
    this._kd = (e) => this._onKey(e, true);
    this._ku = (e) => this._onKey(e, false);
    window.addEventListener("keydown", this._kd);
    window.addEventListener("keyup", this._ku);
  }
  setInput(name, val) { if (name === "left" || name === "right") this.input[name] = val; }

  start() {
    if (this.running) return;
    this.running = true;
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }
  destroy() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("keydown", this._kd);
    window.removeEventListener("keyup", this._ku);
  }
  loop() {
    if (!this.running) return;
    if (!this.ended && !this.paused) this.update();
    this.render();
    this.raf = requestAnimationFrame(this.loop);
  }

  end() {
    if (this.ended) return;
    this.ended = true;
    this.audio?.sfx("win");
    this.cb.onComplete && this.cb.onComplete({ collected: this.collected, total: TOTAL_COINS });
  }

  update() {
    this.frame++;
    if (this.shake > 0) this.shake--;
    if (this.wobble > 0) this.wobble--;

    this.vy = Math.min(this.maxVy, this.vy + 0.008);
    this.depth += this.vy;

    const steer = (this.wobble > 0 ? 3.0 : 7.2);
    if (this.input.left) this.px -= steer;
    if (this.input.right) this.px += steer;
    this.px = Math.max(70, Math.min(WIDTH - 70, this.px));

    // hoof dust behind the bull
    if (this.frame % 2 === 0) this.dust.push({ x: this.px + (Math.random() - 0.5) * 60, y: PLAYER_SCREEN_Y + 40, life: 24, size: 6 + Math.random() * 6 });
    for (const d of this.dust) { d.y += 1.2; d.life--; }
    this.dust = this.dust.filter((d) => d.life > 0);
    for (const s of this.sparks) { s.x += s.vx; s.y += s.vy; s.vy += 0.25; s.life--; }
    this.sparks = this.sparks.filter((s) => s.life > 0);

    for (const c of this.coins) {
      if (c.taken) continue;
      if (Math.abs(c.d - this.depth) < 30 && Math.abs(c.x - this.px) < 46) {
        c.taken = true; this.collected++; this.audio?.sfx("coin");
        for (let k = 0; k < 8; k++) {
          const a = Math.random() * Math.PI * 2, sp = 1 + Math.random() * 3;
          this.sparks.push({ x: c.x, y: PLAYER_SCREEN_Y - 30, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 2, life: 24, color: "#ffd24a" });
        }
      }
    }
    for (const b of this.barrels) {
      if (!b.hit && Math.abs(b.d - this.depth) < 26 && Math.abs(b.x - this.px) < b.r + 30) {
        b.hit = true; this.wobble = 40; this.shake = 12; this.vy = Math.max(4.0, this.vy * 0.6); this.audio?.sfx("hurt");
      }
    }

    this.cb.onProgress && this.cb.onProgress({ collected: this.collected, total: TOTAL_COINS, pct: Math.min(100, (this.depth / FINISH_LINE) * 100) });
    if (this.depth >= FINISH_LINE) this.end();
  }

  // ---------------- Render ----------------
  screenY(d) { return PLAYER_SCREEN_Y + (d - this.depth); }

  render() {
    const ctx = this.ctx;
    ctx.save();
    if (this.shake > 0) ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);

    this.drawArena();

    const finishY = this.screenY(FINISH_LINE);
    if (finishY > -40 && finishY < HEIGHT + 80) this.drawFinish(finishY);

    // barrels
    for (const b of this.barrels) {
      const y = this.screenY(b.d);
      if (y < -50 || y > HEIGHT + 50) continue;
      this.drawBarrel(b.x, y, b.r, b.hit);
    }
    // coins
    for (const c of this.coins) {
      if (c.taken) continue;
      const y = this.screenY(c.d);
      if (y < -30 || y > HEIGHT + 30) continue;
      this.drawCoin(c.x, y);
    }
    // dust
    for (const d of this.dust) {
      ctx.globalAlpha = Math.min(0.5, d.life / 30); ctx.fillStyle = "#c9a878";
      ctx.beginPath(); ctx.arc(d.x, d.y, d.size, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1;
    }

    this.drawBullRider();

    for (const s of this.sparks) {
      ctx.globalAlpha = Math.min(1, s.life / 20); ctx.fillStyle = s.color;
      ctx.fillRect(Math.round(s.x), Math.round(s.y), 4, 4); ctx.globalAlpha = 1;
    }
    ctx.restore();
  }

  drawArena() {
    const ctx = this.ctx;
    // sky strip at top
    const sky = ctx.createLinearGradient(0, 0, 0, 120);
    sky.addColorStop(0, "#f7b26a"); sky.addColorStop(1, "#ffe0a8");
    ctx.fillStyle = sky; ctx.fillRect(0, 0, WIDTH, 120);

    // scrolling dirt ground (tiled photo, mirrored every other tile)
    const img = this.assets.rodeo_ground;
    if (ready(img)) {
      const drawH = WIDTH * (img.naturalHeight / img.naturalWidth);
      const i0 = Math.floor((this.depth - PLAYER_SCREEN_Y) / drawH) - 1;
      for (let i = i0; ; i++) {
        const sy = PLAYER_SCREEN_Y + (i * drawH - this.depth);
        if (sy > HEIGHT) break;
        if (((i % 2) + 2) % 2 === 1) {
          ctx.save(); ctx.translate(0, sy + drawH); ctx.scale(1, -1); ctx.drawImage(img, 0, 0, WIDTH, drawH); ctx.restore();
        } else {
          ctx.drawImage(img, 0, sy, WIDTH, drawH);
        }
      }
    } else {
      const g = ctx.createLinearGradient(0, 0, 0, HEIGHT);
      g.addColorStop(0, "#c79a5b"); g.addColorStop(1, "#9c7038");
      ctx.fillStyle = g; ctx.fillRect(0, 0, WIDTH, HEIGHT);
    }

    // side fences + crowd
    this.drawSideRails();

    // top sky band cover for depth
    const fade = ctx.createLinearGradient(0, 0, 0, 130);
    fade.addColorStop(0, "rgba(255,220,170,0.9)"); fade.addColorStop(1, "rgba(255,220,170,0)");
    ctx.fillStyle = fade; ctx.fillRect(0, 0, WIDTH, 130);
  }

  drawSideRails() {
    const ctx = this.ctx;
    const railW = 74;
    // tiered wooden bleachers on both sides
    for (const sx of [0, WIDTH - railW]) {
      const g = ctx.createLinearGradient(sx, 0, sx + railW, 0);
      g.addColorStop(0, sx === 0 ? "#4a3220" : "#2f2014");
      g.addColorStop(0.5, "#6b4a30");
      g.addColorStop(1, sx === 0 ? "#2f2014" : "#4a3220");
      ctx.fillStyle = g; ctx.fillRect(sx, 0, railW, HEIGHT);
      // bleacher step planks scrolling toward the viewer
      ctx.strokeStyle = "rgba(0,0,0,0.28)"; ctx.lineWidth = 2;
      const step = 34, off = (this.depth * 0.6) % step;
      for (let y = -step + off; y < HEIGHT + step; y += step) { ctx.beginPath(); ctx.moveTo(sx, y); ctx.lineTo(sx + railW, y); ctx.stroke(); }
    }
    // spectators: two columns per side, bodies + heads + hats, bobbing
    for (const p of this.crowd) {
      const y = ((p.y + this.frame * 0.55) % (HEIGHT + 60)) - 20;
      const baseX = p.side === 0 ? 16 : WIDTH - railW + 16;
      const bobY = Math.sin(p.bob + this.frame * 0.08) * 3;
      const x = baseX + p.col * 32 + Math.sin(p.bob + this.frame * 0.05) * 2;
      // shadow
      ctx.fillStyle = "rgba(0,0,0,0.18)"; ctx.beginPath(); ctx.ellipse(x, y + 22 + bobY, 11, 4, 0, 0, Math.PI * 2); ctx.fill();
      // body
      ctx.fillStyle = p.shirt; ctx.beginPath(); ctx.moveTo(x - 9, y + 22 + bobY); ctx.quadraticCurveTo(x, y + 2 + bobY, x + 9, y + 22 + bobY); ctx.closePath(); ctx.fill();
      // head
      ctx.fillStyle = p.skin; ctx.beginPath(); ctx.arc(x, y + bobY, 7, 0, Math.PI * 2); ctx.fill();
      // hat or hair
      if (p.hat) { ctx.fillStyle = p.hatC; ctx.beginPath(); ctx.ellipse(x, y - 5 + bobY, 11, 3.5, 0, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.ellipse(x, y - 7 + bobY, 6, 5, 0, Math.PI, 0); ctx.fill(); }
      else { ctx.fillStyle = "#2a1c12"; ctx.beginPath(); ctx.arc(x, y - 3 + bobY, 6, Math.PI, 2 * Math.PI); ctx.fill(); }
    }
    // wooden front rail + posts
    ctx.fillStyle = "#a9743f"; ctx.fillRect(railW - 10, 0, 12, HEIGHT); ctx.fillRect(WIDTH - railW - 2, 0, 12, HEIGHT);
    ctx.fillStyle = "#8a5a2c";
    const step = 92, off = this.depth % step;
    for (let y = -step + off; y < HEIGHT + step; y += step) {
      ctx.fillRect(railW - 14, y, 22, 16); ctx.fillRect(WIDTH - railW - 6, y, 22, 16);
    }
    // edge shade for depth
    const vg = ctx.createLinearGradient(0, 0, WIDTH, 0);
    vg.addColorStop(0, "rgba(0,0,0,0.32)"); vg.addColorStop(0.5, "rgba(0,0,0,0)"); vg.addColorStop(1, "rgba(0,0,0,0.32)");
    ctx.fillStyle = vg; ctx.fillRect(0, 0, WIDTH, HEIGHT);
  }

  drawFinish(y) {
    const ctx = this.ctx;
    ctx.fillStyle = "#c1121f"; ctx.fillRect(0, y, WIDTH, 16);
    ctx.fillStyle = "#fff";
    for (let x = 0; x < WIDTH; x += 28) ctx.fillRect(x, y, 14, 16);
    ctx.fillStyle = "#FFEB3B"; ctx.font = "800 26px 'Baloo 2', system-ui, sans-serif"; ctx.textAlign = "center";
    ctx.fillText("META", WIDTH / 2, y - 12);
  }

  drawBarrel(x, y, r, hit) {
    const ctx = this.ctx;
    const w = r * 0.95;     // half width at the bulge
    const wt = r * 0.72;    // half width at top/bottom
    const h = r * 1.15;     // half height
    const top = y - h, bot = y + h;
    // ground shadow
    ctx.fillStyle = "rgba(0,0,0,0.38)";
    ctx.beginPath(); ctx.ellipse(x, bot + 4, w * 1.15, r * 0.34, 0, 0, Math.PI * 2); ctx.fill();
    // danger glow ring while live
    if (!hit) {
      const pulse = 0.5 + 0.5 * Math.sin(this.frame * 0.18 + x);
      ctx.strokeStyle = `rgba(255,${120 + pulse * 80},40,${0.35 + pulse * 0.4})`; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.ellipse(x, y, w + 12, h + 12, 0, 0, Math.PI * 2); ctx.stroke();
    }
    // bulging wooden body with a curved wood-tone gradient
    const g = ctx.createLinearGradient(x - w, 0, x + w, 0);
    if (hit) { g.addColorStop(0, "#3a2c20"); g.addColorStop(0.5, "#5a4636"); g.addColorStop(1, "#2e2216"); }
    else { g.addColorStop(0, "#5a3418"); g.addColorStop(0.3, "#b9803f"); g.addColorStop(0.55, "#cf9852"); g.addColorStop(0.78, "#8a5a2c"); g.addColorStop(1, "#4e2e14"); }
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(x - wt, top + 3);
    ctx.quadraticCurveTo(x - w * 1.12, y, x - wt, bot - 3);
    ctx.lineTo(x + wt, bot - 3);
    ctx.quadraticCurveTo(x + w * 1.12, y, x + wt, top + 3);
    ctx.closePath(); ctx.fill();
    ctx.strokeStyle = "#3a2412"; ctx.lineWidth = 2; ctx.stroke();
    // vertical stave seams
    ctx.strokeStyle = "rgba(58,36,18,0.45)"; ctx.lineWidth = 1.5;
    for (let i = -2; i <= 2; i++) {
      const sx = x + i * (wt * 0.55);
      ctx.beginPath(); ctx.moveTo(sx, top + 5); ctx.quadraticCurveTo(sx + i * 2, y, sx, bot - 5); ctx.stroke();
    }
    // steel hoop bands
    const bands = [[top + h * 0.30, w * 0.98], [y, w * 1.06], [bot - h * 0.30, w * 0.98]];
    for (const [by, bw] of bands) {
      ctx.strokeStyle = "#dcd5c6"; ctx.lineWidth = 5;
      ctx.beginPath(); ctx.ellipse(x, by, bw, r * 0.2, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.strokeStyle = "#8a8272"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.ellipse(x, by + 1.6, bw, r * 0.2, 0, 0, Math.PI * 2); ctx.stroke();
    }
    // top lid
    ctx.fillStyle = hit ? "#4a3a2c" : "#7a4f28";
    ctx.beginPath(); ctx.ellipse(x, top + 3, wt, r * 0.26, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "#3a2412"; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = hit ? "#3a2c20" : "#93622f";
    ctx.beginPath(); ctx.ellipse(x, top + 3, wt * 0.68, r * 0.18, 0, 0, Math.PI * 2); ctx.fill();
    // side sheen
    ctx.globalAlpha = 0.16; ctx.fillStyle = "#fff";
    ctx.beginPath(); ctx.ellipse(x - w * 0.45, y, w * 0.17, h * 0.66, 0, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
  }

  drawCoin(x, y) {
    const ctx = this.ctx;
    const spin = Math.abs(Math.sin(this.frame * 0.12 + x * 0.05));
    const rx = 15 * (0.35 + spin * 0.65);
    ctx.save();
    ctx.shadowColor = "rgba(255,210,74,0.7)"; ctx.shadowBlur = 10;
    ctx.fillStyle = "#f2b701"; ctx.beginPath(); ctx.ellipse(x, y, rx, 16, 0, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffe17a"; ctx.beginPath(); ctx.ellipse(x, y, rx * 0.7, 11, 0, 0, Math.PI * 2); ctx.fill();
    if (rx > 8) {
      ctx.fillStyle = "#9a6b00"; ctx.font = "800 13px 'Baloo 2', system-ui, sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("C$", x, y + 1);
    }
    ctx.restore();
  }

  drawBullRider() {
    const bob = Math.sin(this.frame * 0.32) * 8;
    const tilt = (this.input.right ? 0.06 : this.input.left ? -0.06 : 0) + (this.wobble > 0 ? Math.sin(this.frame * 0.6) * 0.12 : 0);
    drawBullRider(this.ctx, { assets: this.assets, char: this.char, cx: this.px, cy: PLAYER_SCREEN_Y + 60 + bob, bullW: 250, tilt });
  }
}
